import {
    requestWithToken as request,
    uploadFile as uf,
} from "./api";
import { directUpload } from "@/api/directUpload";
import { buildQuery } from "@/utils/queryString";

export const getCarteraAnalista = () => request.get(`/analyst_view/get_all/`);


export const allAnalysts = () => request.get(`/analyst_view/analysts_list/`);
export const allCompanies = (all = false, search = "") =>
    request.get(`/analyst_view/companies_list/${buildQuery({ all, search })}`);
export function saveCartera(
    company_id,
    old_analists,
    new_analists,
    deleted_analists,
) {
    const param = {
        old_analists: old_analists,
        new_analists: new_analists,
        deleted_analists: deleted_analists,
    };
    return request.post(`/analyst_view/${company_id}/save_cartera/`, param);
}

// Contraparte analista-céntrica de saveCartera: altas/bajas de compañías de la
// cartera de UN analista en un solo POST atómico (modal AssignAnalysts).
export const saveAnalystCartera = (user_id, add_companies, remove_companies) =>
    request.post(`/analyst_view/${user_id}/save_analyst_cartera/`, {
        add_companies,
        remove_companies,
    });

export async function uploadFile(id, file) {
    return uf(`/analyst_view/${id}/upload_logo/`, file);
}

export const deleteUploadedFile = (idCompany) =>
    request.get(`/analyst_view/${idCompany}/delete_uploaded_file/`);

export const getLedgerCutoff = (company_id) =>
    request.get(`/ledger_cutoff/${company_id}/cutoff/`);

export const setLedgerCutoff = (company_id, ledger_cutoff_date) =>
    request.post(`/ledger_cutoff/${company_id}/set_cutoff/`, {
        ledger_cutoff_date,
    });

// Mes de inicio del Reporte Financiero fijado por el analista (piso de los
// gráficos y filtros de la web). Shape: {report_start_month: "YYYY-MM-01" |
// null, uses_ledger: bool}. `uses_ledger` alimenta la pista del balance sin
// asiento de apertura cuando el mes no es enero.
export const getReportStartMonth = (company_id) =>
    request.get(`/report_start_month/${company_id}/start_month/`);

// report_start_month: "YYYY-MM-DD" (cualquier día: el backend normaliza al
// día 1) | null para limpiar.
export const setReportStartMonth = (company_id, report_start_month) =>
    request.post(`/report_start_month/${company_id}/set_start_month/`, {
        report_start_month,
    });

// Subida del mayor (decisiones §35). Camino directo a S3
// orquestado por api/directUpload.js:
//   1. POST upload_url/ {filename, size, business_type?} → {url, fields, name,
//      ...} (formulario prefirmado, 15 min, tamaño/tipo/clave fijados por la
//      firma). El tipo viaja también acá para que, con el plan de cuentas
//      bloqueado, el 400 llegue ANTES de subir el archivo al bucket (D7).
//   2. POST al bucket con fields + file (sin JWT, con % de progreso).
//   3. POST upload/ {name, business_type, ledger_scope} → 202: el backend
//      valida la referencia (prefijo de la empresa, ticket de un solo uso, HEAD)
//      y encola el import.
// Fallback al multipart de siempre (503 en el paso 1 o rechazo de S3 sin
// bytes enviados). 202 {status:'processing'} — el import corre en RQ; sondear
// con getLedgerLastUpload (last_ledger_import_status: P/S/E).
// 400 (sin archivo / referencia inválida / scope inválido); 409 (import en curso).
export const getLedgerUploadUrl = (company_id, file, businessType) =>
    request.post(`/ledger_cutoff/${company_id}/upload_url/`, {
        filename: file.name,
        size: file.size,
        ...(businessType ? { business_type: businessType } : {}),
    });

export const confirmLedgerUpload = (company_id, name, businessType, scope) =>
    request.post(
        `/ledger_cutoff/${company_id}/upload/`,
        {
            name,
            ...(businessType ? { business_type: businessType } : {}),
            ...(scope ? { ledger_scope: scope } : {}),
        },
        // El backend verifica el objeto en S3 y encola ANTES de responder: con
        // el tope de 30 s por defecto, una respuesta lenta cortaba la promesa
        // con el import ya en curso.
        { timeout: 0 }
    );

export const uploadLedgerMultipart = (
    company_id,
    file,
    businessType,
    scope,
    onUploadProgress
) => {
    const formData = new FormData();
    formData.append("file", file);
    if (businessType) {
        formData.append("business_type", businessType);
    }
    if (scope) {
        formData.append("ledger_scope", scope);
    }
    // El POST sube el archivo (hasta decenas de MB) y el backend lo deja en S3
    // antes de responder 202: con un enlace lento los 30 s por defecto de axios
    // cortaban la subida a mitad de camino. Sin tope acá; el techo real lo pone
    // la infraestructura (router).
    return request.post(`/ledger_cutoff/${company_id}/upload/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        onUploadProgress,
    });
};

export const uploadLedger = (
    company_id,
    file,
    businessType,
    scope,
    { onUploadProgress } = {}
) =>
    directUpload({
        file,
        onUploadProgress,
        label: "mayor",
        getUploadUrl: () => getLedgerUploadUrl(company_id, file, businessType),
        confirm: (name) => confirmLedgerUpload(company_id, name, businessType, scope),
        multipart: (progress) =>
            uploadLedgerMultipart(company_id, file, businessType, scope, progress),
    });

// Metadatos de la última subida del mayor + tipo de negocio actual, para
// mostrarlos y pre-cargar el selector en el modal.
export const getLedgerLastUpload = (company_id) =>
    request.get(`/ledger_cutoff/${company_id}/last_upload/`);

// Subida de la plantilla de Ventas/Costos Devengados: mismo camino directo a
// S3 que el mayor (upload_url → bucket → upload con `name`) con fallback al
// multipart, orquestado por api/directUpload.js. El archivo NO se guarda
// (D11); reemplaza solo los meses presentes en el archivo.
// 202 {status:'processing', run_id} — sondear con getSalesCostsImportStatus
// (status: P/S/E/C). 400 sin archivo / referencia inválida; 409 en curso.
// `confirmReplacement`: el backend detiene la corrida en 'C' si el archivo
// reemplaza meses con documentos o trae advertencias. Para confirmar:
//   - subida directa: la corrida en 'C' conservó el archivo en S3
//     (`file_retained` en el status) → confirmSalesCostsReplacement(run_id),
//     sin volver a subirlo;
//   - multipart: se re-sube el mismo archivo con el flag.
export const getSalesCostsUploadUrl = (company_id, file) =>
    request.post(`/sales_costs_import/${company_id}/upload_url/`, {
        filename: file.name,
        size: file.size,
    });

export const confirmSalesCostsUpload = (company_id, name, confirmReplacement = false) =>
    request.post(
        `/sales_costs_import/${company_id}/upload/`,
        {
            name,
            ...(confirmReplacement ? { confirm_replacement: true } : {}),
        },
        // El backend verifica el objeto en S3 y encola antes de responder.
        { timeout: 0 }
    );

// Confirmación por referencia de una corrida en 'C' cuyo archivo sigue en S3:
// el backend crea la corrida heredera y encola con el flag. 400 si la
// confirmación ya no está disponible (archivo expirado o liberado): volver a
// subir el archivo con confirmReplacement.
export const confirmSalesCostsReplacement = (company_id, run_id) =>
    request.post(
        `/sales_costs_import/${company_id}/upload/`,
        { run_id, confirm_replacement: true },
        { timeout: 0 }
    );

export const uploadSalesCostsMultipart = (
    company_id,
    file,
    confirmReplacement = false,
    onUploadProgress
) => {
    const formData = new FormData();
    formData.append("file", file);
    if (confirmReplacement) {
        formData.append("confirm_replacement", "true");
    }
    // Sin tope: el archivo puede pesar decenas de MB por un enlace lento.
    return request.post(`/sales_costs_import/${company_id}/upload/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        onUploadProgress,
    });
};

export const uploadSalesCostsImport = (
    company_id,
    file,
    confirmReplacement = false,
    { onUploadProgress } = {}
) =>
    directUpload({
        file,
        onUploadProgress,
        label: "ventas/costos",
        getUploadUrl: () => getSalesCostsUploadUrl(company_id, file),
        confirm: (name) => confirmSalesCostsUpload(company_id, name, confirmReplacement),
        multipart: (progress) =>
            uploadSalesCostsMultipart(company_id, file, confirmReplacement, progress),
    });

// Última corrida del import de ventas/costos (con self-heal en el backend):
// {run_id, status, error, invalid_rows, documents_created, rows_parsed,
//  months, uploaded_at, finished_at, progress, file_retained, ...}.
export const getSalesCostsImportStatus = (company_id) =>
    request.get(`/sales_costs_import/${company_id}/import_status/`);

// Elimina los documentos de ventas/costos cargados (sincrónico, sin archivo).
// `months` ('YYYY-MM') vacío = todo lo cargado. Viaja como query param
// repetible (?months=…&months=…), que es lo que lee el backend — el
// serializador de axios usaría `months[]`. 200 {documents_deleted,
// counterparts_deleted, months}; 400 mes mal formado;
// 409 import en curso.
export const deleteSalesCostsDocuments = (company_id, months = []) => {
    const query = months
        .map((month) => `months=${encodeURIComponent(month)}`)
        .join("&");
    return request.delete(
        `/sales_costs_import/${company_id}/documents/${query ? `?${query}` : ""}`
    );
};

// Válvula del bloqueo de las cuentas que alimenta el archivo de Ventas/Costos
// Devengados (docs/DECISIONES.md §15 D6): qué tipos de documento pueden sumar
// igual en esas cuentas. GET devuelve {cv_fed_accounts, unblocked_type_ids,
// types: [{id, name, sii_code, from_sii, documents, unblocked}]}.
export const getSalesCostsUnblockConfig = (company_id) =>
    request.get(`/sales_costs_import/${company_id}/unblock_config/`);

// Reemplaza la lista completa (no es incremental): [] vuelve al bloqueo total.
// 400 si algún id no existe o es un tipo del propio archivo.
export const saveSalesCostsUnblockConfig = (company_id, obligation_type_ids) =>
    request.post(`/sales_costs_import/${company_id}/set_unblock_config/`, {
        obligation_type_ids,
    });

// Visto bueno del analista al plan de cuentas: baja el banner "En revisión"
// del cliente (CompanyDetails.needs_account_plan_review = False).
export const approveAccountPlan = (companyId) =>
    request.post(`/analyst_view/${companyId}/approve_account_plan/`, {});

// ── Candado del plan de cuentas (docs/decisiones §30) ────────────────────
// Bloquea cualquier analista; desbloquea solo el asignado con su correo, el
// código que emite unlock_challenge (5 minutos, un uso) y un motivo.
export const lockAccountPlan = (companyId) =>
    request.post(`/analyst_view/${companyId}/lock_account_plan/`, {});
export const fetchUnlockChallenge = (companyId) =>
    request.post(`/analyst_view/${companyId}/unlock_challenge/`, {});
export const unlockAccountPlan = (companyId, { email, code, reason }) =>
    request.post(`/analyst_view/${companyId}/unlock_account_plan/`, {
        email, code, reason,
    });

// ── Empresa Acumulada (consolidación read-only, docs/DECISIONES.md §17) ──
// Alta: el backend acuña el rut sintético CONS-<pk>; solo viaja el nombre.
export const saveConsolidatedCompany = (name) =>
    request.post(`/analyst_view/save_consolidated_company/`, { name });

// Estado actual para el modal: miembros con sus reglas + usuarios actuales.
export const getConsolidatedMembers = (company_id) =>
    request.get(`/analyst_view/${company_id}/consolidated_members/`);

// Altas/bajas con doble verificación server-driven: sin `confirm` responde
// needs_confirmation + preview (miembros con reglas + usuarios) +
// preview_token; con `confirm` + token aplica, o 409 con preview fresco si
// el estado cambió entre medio.
export const saveConsolidatedMembers = (
    company_id,
    add_members,
    remove_members,
    confirm = false,
    preview_token = "",
) =>
    request.post(`/analyst_view/${company_id}/save_consolidated_members/`, {
        add_members,
        remove_members,
        confirm,
        preview_token,
    });

// Neteo intercompañía y cuentas ignoradas de una consolidada (plan
// consolidadas, decisiones §17). Las cinco funciones hablan con
// CompanyPerAnalistView; el `kind` es netting_balance / netting_results /
// ignore y cada línea es { member_company_id, account_id, side }.
export const getConsolidatedAdjustments = (company_id) =>
    request.get(`/analyst_view/${company_id}/consolidated_adjustments/`);

// Cuentas elegibles por miembro para un lado; `adjustment_id` no excluye las
// del ajuste que se está editando.
export const getConsolidatedAccountOptions = (company_id, { kind, side, adjustment_id } = {}) =>
    request.get(
        `/analyst_view/${company_id}/consolidated_account_options/${buildQuery({ kind, side, adjustment_id })}`,
    );

// Tabla mensual informativa (último año) de un ajuste propuesto; no escribe.
// Con `adjustment_id` (edición) la cadena de saldos restantes sólo mira los
// neteos anteriores a ese.
export const previewConsolidatedAdjustment = (company_id, { kind, lines, adjustment_id = null }) =>
    request.post(`/analyst_view/${company_id}/consolidated_adjustments/preview/`, {
        kind,
        lines,
        adjustment_id,
    });

// Crea un ajuste, o reemplaza sus líneas si viene `adjustment_id`. Responde la
// lista completa más `saved_id`.
export const saveConsolidatedAdjustment = (company_id, { kind, label = "", lines, adjustment_id }) =>
    request.post(`/analyst_view/${company_id}/consolidated_adjustments/`, {
        kind,
        label,
        lines,
        adjustment_id,
    });

export const deleteConsolidatedAdjustment = (company_id, adjustment_id) =>
    request.delete(`/analyst_view/${company_id}/consolidated_adjustments/${adjustment_id}/`);

export function saveCompany(rut, name, isForeign = false) {
    const param = {
        rut: rut,
        name: name,
        is_foreign: isForeign === true,
    };
    return request.post(`/analyst_view/save_company/`, param);
}

export const getDetalleCompania = (id, full) =>
    request.get(`/analyst_view/${id}/complementary_data/${buildQuery({ full })}`);

export const getAccountPlanForCompany = (id) =>
    request.get(
        `/classification_admin_view/${id}/get_account_plan_for_company/`,
    );

export const getCategories = () =>
    request.post(`/classification_user_view/categories/`);

export const hasNewDocsByCompany = () =>
    request.get(`/classification_user_view/has_new_documents_by_company/`);

// `config` (opcional, acá y en get_counterpart / get_ledger_counterparts_page /
// get_account_plan_by_company_ordered) se pasa al request de axios — mismo
// patrón que saveFintocUserHolder. Hoy lo usa store/classification.js para
// viajar el `signal` de AbortController (inerte en axios 0.21; abort real 1.x).
export const get_documents_involved = (
    companies_involved,
    document_type,
    from_day = null,
    to_day = null,
    obligation_name,
    ledger_only = false,
    // Sub-filtro EE.RR/Balance del Libro Mayor ("eerr" | "balance"); null →
    // sin filtro (clasificador SII).
    account_scope = null,
    config = {},
) => {
    const data = {
        companies_involved: companies_involved,
        document_type: document_type,
        from_day,
        to_day,
        obligation_name,
        ledger_only,
        account_scope,
    };
    return request.post(
        `/classification_user_view/documents_involved/`,
        data,
        config,
    );
};

export const resolve_counterpart_by_alias = (alias, document_type) => {
    const data = { alias, document_type };
    return request.post(
        `/classification_user_view/resolve_counterpart_by_alias/`,
        data,
    );
};

export const get_counterpart_monthly_history = (
    ci_id,
    document_type,
    from_day,
    to_day,
    level_2_name = "Ventas",
    level_1_name = null,
    counterpart_type = null,
) => {
    const data = {
        ci_id,
        document_type,
        from_day,
        to_day,
        level_2_name,
        level_1_name,
        counterpart_type,
    };
    return request.post(
        `/classification_user_view/counterpart_monthly_history/`,
        data,
    );
};

// Drawer del Análisis de Ventas: documentos accruados a un RANGO con monto
// prorrateado (matchea el ranking). Endpoint separado de documents_involved
// — este filtra por fecha devengada, no por emitted_at.
//
// El rango lo controla el caller (header del drawer: último año / 6 meses /
// 2 años). Nombre del export se conserva por compatibilidad histórica con
// los consumers — el endpoint sigue siendo `sales_analysis_counterpart_month_docs`.
export const get_counterpart_month_docs = (
    ci_id,
    from_day,
    to_day,
    document_type = "C",
    level_2_name = "Ventas",
    level_1_name = null,
    counterpart_type = null,
) => {
    const data = {
        ci_id,
        init_date: from_day,
        end_date: to_day,
        document_type,
        level_2_name,
        level_1_name,
        counterpart_type,
    };
    return request.post(
        `/classification_user_view/sales_analysis_counterpart_month_docs/`,
        data,
    );
};

// Devengo de todos los pares (doc × cuenta) en una sola request (colapsa el
// N+1 de ChangeAccrual). Devuelve por par { range: [...], ids: [...] } — los
// ids alimentan el borrado por id.
export const get_devengo_batch = (docs_ids, plan_accounts_ids) => {
    return request.post(`/classification_user_view/get_devengo_batch/`, {
        docs_ids,
        plan_accounts_ids,
    });
};

// Borra por id de devengo (los ids los entrega get_devengo_batch): sin
// heurísticas por (doc, cuenta) que dejaban filas inalcanzables (legadas con
// cuenta NULL o huérfanas de una cuenta que ya no clasifica al doc).
export const delete_devengo_by_ids = (accrual_ids) => {
    return request.post(`/classification_user_view/delete_devengo/`, {
        accrual_ids,
    });
};

export const save_devengo = (data) => {
    return request.post(`/classification_user_view/save_devengo/`, data);
};

export const get_counterpart = (
    ci_id,
    pay_or_charge,
    from_day = null,
    to_day = null,
    obligation_name = "Otros",
    config = {},
) => {
    const data = {
        single: true,
        ci_id: ci_id,
        document_type: pay_or_charge,
        from_day,
        to_day,
        obligation_name,
    };
    return request.post(`/classification_user_view/counterparts/`, data, config);
};

// Una PÁGINA de contrapartes, ordenada y filtrada en el servidor
// (docs/DECISIONES.md §29). `ledger_only` elige el origen: true = mayor (vista
// "Data desde ERP", el default histórico), false = SII (Clasificador). Sin
// `page`, el mismo endpoint corre el camino histórico (fetch singular).
// → {page_data, total, page, page_size}
export const get_ledger_counterparts_page = (data, config = {}) =>
    request.post(
        `/classification_user_view/counterparts/`,
        { ledger_only: true, ...data },
        config
    );

// Cuentas y categorías presentes en el rango, para los combos de filtro.
// Mismo origen que la página. El backend lo cachea con la versión de datos.
export const get_ledger_counterpart_facets = (data, config = {}) =>
    request.post(
        `/classification_user_view/counterpart_facets/`,
        { ledger_only: true, ...data },
        config
    );

// Los números de los gráficos del Clasificador sobre TODAS las contrapartes
// del rango (contrapartes, documentos y montos clasificados / sin clasificar),
// con el tipo elegido. Cacheado en el backend con la versión de datos.
// → {dataCounterparts, dataDocuments, dataExpenses, cached}
export const get_counterpart_summary = (data, config = {}) =>
    request.post(
        `/classification_user_view/counterpart_summary/`,
        { ledger_only: false, ...data },
        config
    );

// Años con data del mayor (ERP) de la empresa del usuario (ascendente). La
// vista Data ERP arma con ellos su filtro "Rango de tiempo" (un item por año).
export const getLedgerYears = () =>
    request.get(`/classification_user_view/ledger_years/`);

export const get_account_plan_by_company_ordered = (data, config = {}) => {
    return request.post(
        `/classification_user_view/get_account_plan_by_company_ordered/`,
        data,
        config,
    );
};

export const get_prorates_x_company_involved = (
    company_involved_id,
    document_id_selected,
    document_type,
    obligation_name,
) => {
    const data = {
        company_involved_id: company_involved_id,
        document_id_selected: document_id_selected,
        document_type: document_type,
        obligation_name: obligation_name,
    };

    return request.post(
        `/classification_user_view/get_prorates_x_company_involved/`,
        data,
    );
};

// Detalle completo del devengo de un documento (cada fila: cuenta × mes × %),
// para el visualizador read-only del mayor.
export const get_doc_accruals = (doc_id) =>
    request.post(`/classification_user_view/get_doc_accruals/`, { doc_id });

export const save_analysts_per_company = (company_id, data) =>
    request.post(
        `/analyst_view/${company_id}/save_analysts_per_company/`,
        data,
    );

export const save_companies_analyst = (user_id, data) =>
    request.post(`/analyst_view/${user_id}/save_companies_analyst/`, data);

export const companies_per_user = (user_id) =>
    request.get(`/analyst_view/${user_id}/companies_per_user`);

export const users_of_company = (company_id) =>
    request.get(`/analyst_view/${company_id}/users_of_company`);

// Cola de solicitudes de alta de empresa (Opción B) — analista/staff.
export const linkRequests = () =>
    request.get(`/analyst_view/link_requests/`);

export const approveLinkRequest = (id) =>
    request.post(`/analyst_view/${id}/approve_link_request/`);

export const rejectLinkRequest = (id, reason) =>
    request.post(`/analyst_view/${id}/reject_link_request/`, { reason });

// Gestión de terceros: conexiones Fintoc (SII + bancos) de la empresa con el
// estado real en Fintoc y veredicto de reconciliación; desconexión por cpl_id
// (desactiva primero en Fintoc, después en la BD).
export const getCompanyConnections = (company_id) =>
    request.get(`/analyst_view/${company_id}/connections/`);

export const disconnectCompanyConnection = (company_id, cpl_id, local_only = false) =>
    request.post(`/analyst_view/${company_id}/disconnect_connection/`, {
        cpl_id,
        local_only,
    });

// Corre a demanda la misma reconciliación org-wide del cron semanal
// (persiste veredicto + estado de Fintoc por link).
export const reconcileCompanyConnections = (company_id) =>
    request.post(`/analyst_view/${company_id}/reconcile_connections/`, {});

// Vista de desfases: listado org-wide de links con veredicto de desfase
// persistido (DB_ON_FINTOC_OFF / DB_OFF_FINTOC_ON / MISSING_IN_FINTOC) —
// lee BD, no llama a Fintoc. reconcileAllConnections refresca a demanda.
export const getConnectionsMismatches = () =>
    request.get(`/analyst_view/connections_mismatches/`);

export const reconcileAllConnections = () =>
    request.post(`/analyst_view/reconcile_connections_all/`, {});

export const save_prorate_tree = (params) =>
    request.post(`/classification_user_view/save_prorate_tree/`, params);

export const delete_prorate_by_company_involved = (data) =>
    request.post(
        `/classification_user_view/delete_prorate_by_company_involved/`,
        data,
    );

// balance_concept ("Cuenta X" de los KPIs de Balance) es opcional: undefined
// no viaja (el backend no toca el valor guardado); null/"" limpian.
export const createAccount = (
    companyId,
    level_2_id,
    cuenta_name,
    order,
    balance_concept,
) => {
    const params = { level_2_id: level_2_id, cuenta_name: cuenta_name, order };
    if (balance_concept !== undefined) {
        params.balance_concept = balance_concept || null;
    }
    return request.post(
        `/classification_admin_view/${companyId}/create_account_for_company/`,
        params,
    );
};
export const editAccount = (
    companyId,
    account_id,
    level_2_id,
    cuenta_name,
    order,
    balance_concept,
) => {
    const params = {
        account_id,
        level_2_id: level_2_id,
        cuenta_name: cuenta_name,
        order,
    };
    if (balance_concept !== undefined) {
        params.balance_concept = balance_concept || null;
    }
    return request.post(
        `/classification_admin_view/${companyId}/edit_account_for_company/`,
        params,
    );
};

export const deleteAccount = (companyId, account_id) => {
    const params = {
        account_id,
    };
    return request.post(
        `/classification_admin_view/${companyId}/delete_plan_account_company/`,
        params,
    );
};

export const saveAccountPlans = (id, checked, already_saved) => {
    const params = { raw_account_plans: checked, already_saved: already_saved };
    return request.post(
        `/classification_admin_view/${id}/save_account_plans_for_company/`,
        params,
    );
};

export const makeUniquePlanAccounts = (id_company) => {
    return request.post(
        `/classification_admin_view/${id_company}/make_unique_plan_accounts_by_company/`,
    );
};

export const upload_all_plan_accounts = (id_company, fdFile) => {
    // Header multipart explícito: la instancia con Content-Type JSON
    // serializa el FormData a '{}' y el backend responde 415 (mismo motivo
    // que compareTemplateExcel).
    return request.post(
        `/classification_admin_view/${id_company}/upload_plan_accounts_file/`,
        fdFile,
        { headers: { "Content-Type": "multipart/form-data" } },
    );
};

export const sendEmailDocs = (company, userid, which) => {
    const params = { userid: userid, which: which };
    return request.post(`/analyst_view/${company}/send_email_docs/`, params);
};

export const updateGeneralBookSheetId = (id_drive_file, company_id) =>
    request.post("/analyst_view/update_general_book_drive_id/", {
        id_drive_file,
        company_id,
    });

export const getUsersByCompany = (company_id) =>
    request.get(`/analyst_view/${company_id}/users_by_company/`, {});

export const updateUserOperations = (company_id, userOps) =>
    request.post(
        `/analyst_view/${company_id}/update_user_operations/`,
        userOps,
    );

export const saveTipoNegocio = (company_id, checked, classify_by_sales) =>
    request.post("/analyst_view/save_business_type/", {
        company_id,
        checked,
        classify_by_sales,
    });

// Validador super_type ↔ clasificación (H1/F6): warning cuando la
// clasificación real de costos no calza con el tipo de negocio elegido.
export const getBusinessTypeClassificationCheck = (company_id) =>
    request.get(`/analyst_view/${company_id}/business_type_classification_check/`);

export const getAttributes = (company_id) =>
    request.get(`/attributes/${buildQuery({ company_id })}`);

export const addAttributes = (attribute) =>
    request.post(`/attributes/`, attribute);

export const deleteAttributes = (attribute, company_id) =>
    request.delete(`/attributes/${attribute.id}`, { data: { company_id } });

// ── Plantillas estándar de plan de cuentas (por rubro) ──────────────────────
// Backend: /account_plan_templates/ (views/analyst/account_plan_template_view).
// Las plantillas nunca se asocian a una empresa (invariante I11): editar acá
// no toca datos de clientes.
export const getTemplateBusinessTypes = () =>
    request.get(`/account_plan_templates/business_types/`);

export const getTemplateAccountPlan = (businessTypeId) =>
    request.get(`/account_plan_templates/${businessTypeId}/accounts/`);

export const createTemplateAccount = (
    businessTypeId,
    level_2_id,
    name,
    order,
    balance_concept = null,
) =>
    request.post(`/account_plan_templates/${businessTypeId}/create_account/`, {
        level_2_id,
        name,
        order,
        balance_concept: balance_concept || null,
    });

export const editTemplateAccount = (
    businessTypeId,
    account_plan_id,
    level_2_id,
    name,
    order,
    balance_concept = null,
) =>
    request.post(`/account_plan_templates/${businessTypeId}/edit_account/`, {
        account_plan_id,
        level_2_id,
        name,
        order,
        balance_concept: balance_concept || null,
    });

export const deleteTemplateAccount = (businessTypeId, account_plan_id) =>
    request.post(`/account_plan_templates/${businessTypeId}/delete_account/`, {
        account_plan_id,
    });

export const getTemplateSharedAccounts = () =>
    request.get(`/account_plan_templates/shared_accounts/`);

export const splitTemplateSharedAccounts = (account_plan_ids = null) =>
    request.post(
        `/account_plan_templates/split_shared_accounts/`,
        account_plan_ids ? { account_plan_ids } : {},
    );

// uf (uploadFile) y no request.post: la instancia con Content-Type JSON
// serializa el FormData a '{"file":{}}' en vez de mandarlo multipart.
export const compareTemplateExcel = (file) =>
    uf(`/account_plan_templates/compare_excel/`, file);

export const applyTemplateExcelDiff = (operations) =>
    request.post(`/account_plan_templates/apply_excel_diff/`, { operations });

// Tabla de hechos del reporte financiero (precálculo por empresa, panel del
// analista). status: {report_mode, status P/S/E, is_fresh, built_at, rows,
// error, job_running, is_consolidated}.
export const getReportFactStatus = (company_id) =>
    request.get(`/report_fact/${company_id}/status/`);

// mode: "L" (en vivo) | "P" (precalculado). Al pasar a "P" sin tabla el
// backend encola el primer recálculo.
export const setReportMode = (company_id, mode) =>
    request.post(`/report_fact/${company_id}/mode/`, { mode });

// 202 encolado; 409 ya hay uno en curso; 400 empresa consolidada.
export const rebuildReportFact = (company_id) =>
    request.post(`/report_fact/${company_id}/rebuild/`);
